import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import API_BASE from '../apiBase';
import { auth, getIdTokenSafe } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Toast from './Toast';

const NotificationsContext = createContext(null);

function toWsBase(apiBase) {
  try {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.origin;
  } catch {
    // Fallback for relative
    if (apiBase.startsWith('https://')) return apiBase.replace('https://', 'wss://');
    if (apiBase.startsWith('http://')) return apiBase.replace('http://', 'ws://');
    return 'ws://localhost:8080';
  }
}

export function NotificationsProvider({ children }) {
  const [toasts, setToasts] = useState({}); // request_id -> event
  const [projectNames, setProjectNames] = useState({}); // project_id -> project_name
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const [authed, setAuthed] = useState(false);
  const visibilityRef = useRef(document.visibilityState);
  const fetchedProjectIdsRef = useRef(new Set());

  const wsUrl = useMemo(() => {
    const base = toWsBase(API_BASE);
    return `${base}/api/ws/notifications`;
  }, []);

  const connect = useCallback(async () => {
    // Gate on auth and visibility
    if (!authed) return;
    if (document.visibilityState === 'hidden') return;
    // Skip if already connecting/open
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    try {
      const token = await getIdTokenSafe();
      if (!token) {
        // No token yet; try again shortly
        const delay = Math.min(10000, 500 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
        return;
      }
      console.log('[WS] connecting to', wsUrl);
      const protocols = [token];
      wsRef.current = new WebSocket(wsUrl, protocols);
      wsRef.current.onopen = () => {
        console.log('[WS] open');
        retryRef.current = 0;
      };
      wsRef.current.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          // Expected shape: { type, request_id, project_id, pr_number, stage, status, message, ts }
          if (!msg || !msg.request_id) return;
          console.log('[WS] message', msg);
          // Opportunistically fetch project name once
          if (msg.project_id && !fetchedProjectIdsRef.current.has(msg.project_id)) {
            fetchedProjectIdsRef.current.add(msg.project_id);
            // Use axios (interceptor attaches ID token)
            import('axios').then(({ default: ax }) => {
              ax
                .get(`${API_BASE}/api/projects/${encodeURIComponent(msg.project_id)}`)
                .then((r) => r?.data || null)
                .then((data) => {
                  const name = data?.name || data?.project?.name;
                  if (name) {
                    setProjectNames((prev) => ({ ...prev, [msg.project_id]: name }));
                  }
                })
                .catch(() => {});
            });
          }
          setToasts((prev) => {
            const next = { ...prev };
            next[msg.request_id] = msg;
            return next;
          });
        } catch {
          // ignore
        }
      };
      wsRef.current.onclose = () => {
        console.log('[WS] close');
        if (!authed) return; // Do not auto-retry if logged out
        if (document.visibilityState === 'hidden') return; // Pause while hidden
        const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      };
      wsRef.current.onerror = (e) => {
        console.log('[WS] error', e);
        wsRef.current && wsRef.current.close();
      };
    } catch {
      const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
      setTimeout(connect, delay);
    }
  }, [authed, wsUrl]);

  useEffect(() => {
    // Track auth state
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);
      if (!user && wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!wsUrl) return;
    let closed = false;

    // Visibility pause/resume
    const onVisibility = () => {
      visibilityRef.current = document.visibilityState;
      if (document.visibilityState === 'hidden') {
        if (wsRef.current) {
          wsRef.current.close();
        }
      } else if (document.visibilityState === 'visible') {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Initial connect (auth-gated)
    connect();

    return () => {
      closed = true;
      wsRef.current && wsRef.current.close();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [wsUrl, authed, connect]);

  // Auto-dismiss finished or failed after 20s
  useEffect(() => {
    const id = setInterval(() => {
      setToasts((prev) => {
        const now = Date.now();
        const next = { ...prev };
        Object.entries(prev).forEach(([key, e]) => {
          const done = e?.stage === 5 || e?.status === 'failed';
          const ts = e?.ts ? new Date(e.ts).getTime() : now;
          // Inactivity timeout: 10 minutes without updates => mark failed
          if (!done && now - ts > 10 * 60 * 1000) {
            next[key] = {
              ...e,
              status: 'failed',
              message: 'Something went wrong. Please retry.',
              stage: e?.stage || 2,
              ts: new Date().toISOString()
            };
            return;
          }
          if (done && now - ts > 20000) {
            delete next[key];
          }
        });
        return next;
      });
    }, 5000);
    return () => clearInterval(id);
  }, []);

  // Expose minimal API if needed later
  const value = useMemo(() => ({}), []);

  return (
    <NotificationsContext.Provider value={value}>
      {/* Toast container */}
      <Box
        sx={{
          position: 'fixed',
          top: 12,
          right: 12,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          pointerEvents: 'none'
        }}
      >
        {Object.values(toasts).map((e) => {
          // Build log prefix: <pr>-<projectName>-<repoFullName> parsed from request_id
          let prefix = '-';
          try {
            const req = String(e.request_id || '');
            const parts = req.split('-');
            if (parts.length >= 3) {
              const ts = parts.pop(); // timestamp
              const pr = parts.pop(); // pr number
              const repo = parts.join('-'); // remaining is repo full name
              const projectName = projectNames[e.project_id] || e.project_name;
              if (pr && repo) {
                prefix = `${pr}-${projectName || e.project_id || ''}-${repo}`;
              }
            }
          } catch {}
          return (
          <Box key={e.request_id} sx={{ pointerEvents: 'auto' }}>
            <Toast
              title={`PR #${e.pr_number} • ${e.status === 'failed' ? 'Failed' : e.stage === 5 ? 'Completed' : 'In progress'}`}
              message={(e.status === 'failed' || e.type === 'review.error') ? 'Please look at the details below' : (e.message || 'Processing...')}
              details={(e.status === 'failed' || e.type === 'review.error') ? (e.message || '') : ''}
              stage={Number(e.stage) || 1}
              error={e.status === 'failed' || e.type === 'review.error'}
              logPrefix={prefix}
              onClose={() => {
                setToasts((prev) => {
                  const next = { ...prev };
                  delete next[e.request_id];
                  return next;
                });
              }}
            />
          </Box>
          );
        })}
      </Box>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}